import axios from 'axios'

// API configuration
const wikipediaApi = axios.create({
  baseURL: process.env.REACT_APP_WIKIPEDIA_API_URL || 'https://en.wikipedia.org/api/rest_v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Api-User-Agent': 'Historer/1.0 (https://historer.com; contact@historer.com)'
  }
})

const wikidataApi = axios.create({
  baseURL: process.env.REACT_APP_WIKIDATA_API_URL || 'https://www.wikidata.org/w/api.php',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
})

const openStreetMapApi = axios.create({
  baseURL: process.env.REACT_APP_OPENSTREETMAP_API_URL || 'https://overpass-api.de/api/interpreter',
  timeout: 10000,
  headers: {
    'Content-Type': 'text/plain',
  }
})

const nominatimApi = axios.create({
  baseURL: process.env.REACT_APP_NOMINATIM_API_URL || 'https://nominatim.openstreetmap.org/search',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
})

// Wikipedia API services
export const wikipediaAPI = {
  // Search Wikipedia articles
  searchArticles: async (query, limit = 10) => {
    try {
      const response = await wikipediaApi.get(`/page/summary/${encodeURIComponent(query)}`)
      return response.data
    } catch (error) {
      console.error('Wikipedia search error:', error)
      return null
    }
  },

  // Get article summary
  getArticleSummary: async (title) => {
    try {
      const response = await wikipediaApi.get(`/page/summary/${encodeURIComponent(title)}`)
      return response.data
    } catch (error) {
      console.error('Wikipedia summary error:', error)
      return null
    }
  },

  // Get article content
  getArticleContent: async (title) => {
    try {
      const response = await wikipediaApi.get(`/page/html/${encodeURIComponent(title)}`)
      return response.data
    } catch (error) {
      console.error('Wikipedia content error:', error)
      return null
    }
  },

  // Search for multiple articles
  searchMultiple: async (query, limit = 20) => {
    try {
      const response = await wikipediaApi.get(`/page/related/${encodeURIComponent(query)}`)
      return response.data
    } catch (error) {
      console.error('Wikipedia multiple search error:', error)
      // Fallback to Action API with CORS support
      return await this.searchWithActionAPI(query, limit)
    }
  },

  // Fallback search using Action API with CORS support
  searchWithActionAPI: async (query, limit = 20) => {
    try {
      // Use JSONP-style CORS-safe approach
      const response = await axios.get('https://en.wikipedia.org/w/api.php', {
        params: {
          action: 'query',
          format: 'json',
          list: 'search',
          srsearch: query,
          srlimit: limit,
          origin: '*',
          utf8: 1,
          redirects: 1
        },
        headers: {
          'Api-User-Agent': 'Historer/1.0 (https://historer.com; contact@historer.com)',
          'Accept': 'application/json'
        },
        timeout: 15000
      })
      
      // Transform Action API response to match REST API format
      const searchResults = response.data.query?.search || []
      return {
        pages: searchResults.map(page => ({
          id: page.pageid,
          title: page.title,
          extract: page.snippet?.replace(/<[^>]*>/g, '') || '', // Remove HTML tags
          thumbnail: null,
          url: `https://en.wikipedia.org/wiki/${encodeURIComponent(page.title)}`,
          coordinates: null,
          source: 'wikipedia'
        }))
      }
    } catch (error) {
      console.error('Wikipedia Action API search error:', error)
      // Return empty array instead of crashing
      return { pages: [] }
    }
  }
}

// Wikidata API services
export const wikidataAPI = {
  // Search Wikidata entities
  searchEntities: async (search, type = 'item', limit = 10) => {
    try {
      const response = await wikidataApi.get('', {
        params: {
          action: 'wbsearchentities',
          search,
          language: 'en',
          format: 'json',
          type,
          limit,
          origin: '*'
        }
      })
      return response.data.search || []
    } catch (error) {
      console.error('Wikidata search error:', error)
      return []
    }
  },

  // Get entity data
  getEntity: async (id) => {
    try {
      const response = await wikidataApi.get('', {
        params: {
          action: 'wbgetentities',
          ids: id,
          format: 'json',
          origin: '*'
        }
      })
      return response.data.entities?.[id]
    } catch (error) {
      console.error('Wikidata entity error:', error)
      return null
    }
  },

  // Get monuments with coordinates
  getMonumentsWithCoordinates: async (bounds) => {
    const query = `
      [out:json][timeout:25];
      (
        node["historic"~"monument|memorial|castle|archaeological_site|ruins"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
        way["historic"~"monument|memorial|castle|archaeological_site|ruins"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
        relation["historic"~"monument|memorial|castle|archaeological_site|ruins"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
      );
      out geom;
    `
    
    try {
      const response = await openStreetMapApi.post('', query, {
        headers: {
          'Content-Type': 'text/plain'
        }
      })
      return response.data.elements || []
    } catch (error) {
      console.error('OpenStreetMap monuments error:', error)
      return []
    }
  }
}

// OpenStreetMap/Overpass API services
export const openStreetMapAPI = {
  // Get monuments within bounds
  getMonumentsInBounds: async (bounds) => {
    const query = `
      [out:json][timeout:25];
      (
        node["historic"]["name"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
        way["historic"]["name"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
      );
      out geom;
    `
    
    try {
      const response = await openStreetMapApi.post('', query, {
        headers: {
          'Content-Type': 'text/plain'
        }
      })
      return response.data.elements || []
    } catch (error) {
      console.error('OSM monuments error:', error)
      return []
    }
  },

  // Search by location name
  searchByLocation: async (location) => {
    try {
      const response = await nominatimApi.get('', {
        params: {
          q: location,
          format: 'json',
          addressdetails: 1,
          limit: 5
        }
      })
      return response.data || []
    } catch (error) {
      console.error('Location search error:', error)
      return []
    }
  },

  // Get nearby monuments
  getNearbyMonuments: async (lat, lon, radius = 10000) => {
    const query = `
      [out:json][timeout:25];
      (
        node["historic"]["name"](around:${radius},${lat},${lon});
        way["historic"]["name"](around:${radius},${lat},${lon});
      );
      out geom;
    `
    
    try {
      const response = await openStreetMapApi.post('', query, {
        headers: {
          'Content-Type': 'text/plain'
        }
      })
      return response.data.elements || []
    } catch (error) {
      console.error('Nearby monuments error:', error)
      return []
    }
  }
}

// Combined monument search service
export const monumentAPI = {
  // Search monuments by keyword
  searchMonuments: async (query, filters = {}) => {
    const results = []
    
    try {
      // Validate input
      if (!query || typeof query !== 'string') {
        return []
      }

      // Search Wikipedia for related articles with error handling
      try {
        const wikiResults = await wikipediaAPI.searchMultiple(query)
        if (wikiResults && wikiResults.pages) {
          results.push(...wikiResults.pages.map(page => ({
            id: page.id || page.pageid,
            title: page.title || page.titles?.normalized || 'Unknown',
            description: page.description || '',
            extract: page.extract || '',
            thumbnail: page.thumbnail?.source || null,
            coordinates: page.coordinates || null,
            source: 'wikipedia',
            url: page.url || page.content_urls?.desktop?.page || '#'
          })))
        }
      } catch (wikiError) {
        console.warn('Wikipedia search failed, continuing with other sources:', wikiError.message)
      }
      
      // Search Wikidata for entities with error handling
      try {
        const wikidataResults = await wikidataAPI.searchEntities(query, 'item', 20)
        if (wikidataResults && Array.isArray(wikidataResults)) {
          for (const entity of wikidataResults) {
            if (entity.label && entity.description) {
              results.push({
                id: entity.id,
                title: entity.label,
                description: entity.description,
                source: 'wikidata',
                wikidataId: entity.id
              })
            }
          }
        }
      } catch (wikidataError) {
        console.warn('Wikidata search failed, continuing with other sources:', wikidataError.message)
      }
      
      // Filter by era if specified
      if (filters.era && typeof filters.era === 'string') {
        return results.filter(item => 
          (item.description && item.description.toLowerCase().includes(filters.era.toLowerCase())) ||
          (item.title && item.title.toLowerCase().includes(filters.era.toLowerCase()))
        )
      }
      
      return results
    } catch (error) {
      console.error('Monument search error:', error)
      // Return empty array instead of crashing
      return []
    }
  },

  // Get monument details
  getMonumentDetails: async (id, source = 'wikipedia') => {
    try {
      if (source === 'wikipedia') {
        const summary = await wikipediaAPI.getArticleSummary(id)
        if (summary) {
          return {
            ...summary,
            source: 'wikipedia'
          }
        }
      } else if (source === 'wikidata') {
        const entity = await wikidataAPI.getEntity(id)
        if (entity) {
          return {
            id: entity.id,
            title: entity.labels?.en?.value,
            description: entity.descriptions?.en?.value,
            claims: entity.claims,
            source: 'wikidata'
          }
        }
      }
      return null
    } catch (error) {
      console.error('Monument details error:', error)
      return null
    }
  },

  // Get monuments in map bounds
  getMonumentsInBounds: async (bounds) => {
    try {
      const osmResults = await openStreetMapAPI.getMonumentsInBounds(bounds)
      
      return osmResults.map(element => ({
        id: element.id,
        title: element.tags?.name || 'Unknown Monument',
        description: element.tags?.historic || 'Historic Monument',
        coordinates: element.lat && element.lon ? [element.lon, element.lat] : null,
        type: element.type,
        tags: element.tags,
        source: 'openstreetmap'
      })).filter(monument => monument.coordinates)
    } catch (error) {
      console.error('Bounds monuments error:', error)
      return []
    }
  },

  // Get nearby monuments
  getNearbyMonuments: async (lat, lon, radius = 10000) => {
    try {
      const nearbyResults = await openStreetMapAPI.getNearbyMonuments(lat, lon, radius)
      
      return nearbyResults.map(element => ({
        id: element.id,
        title: element.tags?.name || 'Unknown Monument',
        description: element.tags?.historic || 'Historic Monument',
        coordinates: element.lat && element.lon ? [element.lon, element.lat] : null,
        type: element.type,
        tags: element.tags,
        source: 'openstreetmap'
      })).filter(monument => monument.coordinates)
    } catch (error) {
      console.error('Nearby monuments error:', error)
      return []
    }
  }
}

// Era classification helper
export const classifyEra = (description, title = '') => {
  const text = `${title} ${description}`.toLowerCase()
  
  const eraPatterns = {
    'ancient': ['ancient', 'prehistoric', 'neolithic', 'bronze age', 'iron age', 'classical antiquity'],
    'classical': ['classical', 'greek', 'roman', 'byzantine', 'hellenistic'],
    'medieval': ['medieval', 'middle ages', 'norman', 'gothic', 'viking', 'crusade'],
    'early modern': ['early modern', 'renaissance', 'baroque', 'rococo', 'enlightenment'],
    'modern': ['modern', 'victorian', 'industrial', 'art nouveau', 'art deco'],
    'contemporary': ['contemporary', 'modernist', 'postmodern', '21st century', '20th century']
  }
  
  for (const [era, patterns] of Object.entries(eraPatterns)) {
    if (patterns.some(pattern => text.includes(pattern))) {
      return era
    }
  }
  
  return 'unknown'
}
